import { pointAtStation, type Alignment, type DrapeSampler } from '@/core/alignment';

import { sampleStations } from './swathGeometry';

/** How far either side of the centreline `fitSwathToSurface` looks. */
export const FIT_SWATH_SCAN_M = 200;
/** Offset step of the scan, and the margin added outside the last hit. */
export const FIT_SWATH_STEP_M = 0.5;
/** The dialog's own bound on a swath side (`refineStationVolumes`). */
const MAX_SWATH_M = 500;

export interface FittedSwath {
  /** Metres left of the centreline, rounded outward to `FIT_SWATH_STEP_M`. */
  left: number;
  right: number;
  /** How many station lines the surface was found on, of how many scanned. */
  stationsHit: number;
  stationsScanned: number;
}

/** `v` rounded UP to the next multiple of `FIT_SWATH_STEP_M`, plus one step of
 *  margin, never past the dialog's bound. A swath that ends exactly on the
 *  surface's edge clips the outermost cells the worker samples there. */
export const roundSwathOutward = (v: number): number =>
  Math.min(MAX_SWATH_M, Math.ceil(Math.max(0, v) / FIT_SWATH_STEP_M) * FIT_SWATH_STEP_M + FIT_SWATH_STEP_M);

/**
 * The swath a run should use so that its corridor is the surface being
 * measured and nothing more: scan the surface's sampler along perpendiculars
 * through the alignment and take the outermost offsets it answers at.
 *
 * Only a LandXML surface has a sampler in the browser (`crestSourcesAtom`),
 * so this is the pre-run fit; after a run the worker's
 * `summary.referenceExtentLeftM/RightM` (see `fittedSwathFromExtents`) does
 * the same job for every surface type. Returns `null` when the surface is
 * never found along the range — a wrong alignment, or a surface that does
 * not reach it within `FIT_SWATH_SCAN_M`.
 *
 * The sampler resolves every `<Surface>` of the layer, so a Civil 3D export
 * that ships "Existing Ground" beside the design fits to the wider of the two.
 */
export function fitSwathToSurface(
  a: Alignment,
  sampler: DrapeSampler,
  fromStation: number,
  toStation: number,
): FittedSwath | null {
  const from = Math.max(0, Math.min(fromStation, toStation));
  const to = Math.min(a.length, Math.max(fromStation, toStation));
  // ~200 stations along the range (plus every vertex), never closer than 1 m
  // and never further apart than 10 m: 200 × 800 samples is a click, not a wait.
  const step = Math.min(10, Math.max(1, (to - from) / 200));
  const stations = sampleStations(a, from, to, step);
  const n = Math.round(FIT_SWATH_SCAN_M / FIT_SWATH_STEP_M);
  let minOffset = Number.POSITIVE_INFINITY;
  let maxOffset = Number.NEGATIVE_INFINITY;
  let hit = 0;
  for (let i = 0; i < stations.length; i++) {
    const p = pointAtStation(a, stations[i]);
    // Left normal of the heading is (-ty, tx); left is a NEGATIVE offset
    // (`projectPoint`), so the point at offset o is p - n·o.
    const nx = -p.ty;
    const ny = p.tx;
    let any = false;
    for (let j = -n; j <= n; j++) {
      const o = j * FIT_SWATH_STEP_M;
      if (sampler(p.x - nx * o, p.y - ny * o) == null) continue;
      any = true;
      if (o < minOffset) minOffset = o;
      if (o > maxOffset) maxOffset = o;
    }
    if (any) hit++;
  }
  if (hit === 0) return null;
  return {
    left: roundSwathOutward(-minOffset),
    right: roundSwathOutward(maxOffset),
    stationsHit: hit,
    stationsScanned: stations.length,
  };
}

/**
 * The post-run fit: the reference's reach the worker measured
 * (`StationSummary.referenceExtentLeftM/RightM`), rounded outward the same
 * way. `null` when the run carries no extents (older run, or nothing covered).
 */
export function fittedSwathFromExtents(
  left: number | null | undefined,
  right: number | null | undefined,
): { left: number; right: number } | null {
  if (left == null || right == null || !Number.isFinite(left) || !Number.isFinite(right)) return null;
  return { left: roundSwathOutward(left), right: roundSwathOutward(right) };
}

/**
 * Below this share of the swath the dock points out that the swath is wider
 * than the surface being measured and offers the fitted one. Three quarters:
 * a design that fills less than that leaves a band wider than a lane empty on
 * at least one side.
 */
export const SWATH_UNDERUSED_PCT = 75;
